import { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { DemoWalkthrough } from '../components/DemoWalkthrough';
import { TenantFooterPlaceholder } from '../components/TenantFooterPlaceholder';
import { useModeStore } from '../state/mode.store';
import { useTenantStore } from '../state/tenant.store';
import { useUserStore } from '../state/user.store';
import { usePatientStore } from '../state/patient.store';
import { getPatientById } from '../data/synthetic-patients';
import { emitTelemetry, newCorrelationId } from '../services/telemetry';
import { TopBar } from './TopBar';
import { TabNav } from './TabNav';
import { TABS, type TabId } from './tabs';

/**
 * Brand Patch (vault: 06-decisions/care-pathway-studio/2026-05-06-brand-
 * and-naming-cleanup-patch.md): clinical pathway display names.
 *
 * Severity-1 fix: the prior map paired TLP2-TLP4 with the wrong clinical
 * arcs ("Hypertension Control", "Diabetes Care", "Lipid Management"),
 * which was the Sara-test failure mode root cause. The correct mapping
 * follows the Brand Patch table.
 */
const PATHWAY_DISPLAY_NAMES: Record<string, string> = {
  'cardiometabolic-pathway': 'Cardiometabolic Pathway',
  'hrt-pathway': 'HRT Pathway',
  'metabolic-syndrome-pathway': 'Metabolic Syndrome Pathway',
  'cvd-prevention-pathway': 'CVD Prevention Pathway',
};

/**
 * AppShell - root layout (PROD-578).
 *
 * Renders the TopBar, the 8-tab TabNav, and the routed `<Outlet />` for the
 * active tab's view. Wraps the tree in ErrorBoundary and renders the
 * TenantFooterPlaceholder at the root per PROD-567 wiring.
 *
 * Per HC9 + COMPONENT-CONTRACTS §1, AppShell emits two telemetry events:
 *   - `app_init`   on first mount (with tenantDefault, mode, userId,
 *                  entryTab, correlation_id). `entryTab` (PROD-582) is
 *                  the tab id resolved from the entry URL so deep-link
 *                  landings stay observable when the route-change guard
 *                  short-circuits the first `tab_change` (from === to).
 *   - `tab_change` on every tab change (with from_tab, to_tab, latency_ms,
 *                  correlation_id)
 *
 * Phase 1 telemetry is the stub at src/services/telemetry/index.ts
 * (console.info with `[TELEMETRY-STUB]` prefix). PROD-573 swaps the stub
 * for the real Application Insights sink without touching this file.
 *
 * The breadcrumb separator is U+00B7 (·, middle dot), never an em dash, per
 * HC5.
 */
/**
 * Map a pathname to the active tab id (PROD-581).
 *
 * Forward-compat tightening: match is exact on the tab path or a proper
 * subpath delimited by `/`. A bare `startsWith(t.path)` would silently
 * false-match a future sibling route like `/architecture-templates`
 * against the `/architecture` tab. The unified form below also handles the
 * root tab correctly because `pathname.startsWith('//')` is never true for
 * a normalized React Router pathname.
 *
 * Returns the matched tab id, or `'studio'` as the default landing tab when
 * no manifest entry matches (caller-side default).
 */
function pathToTabId(pathname: string): TabId {
  const match = TABS.find(
    (t) => pathname === t.path || pathname.startsWith(t.path + '/'),
  );
  return match ? match.id : 'studio';
}

export function AppShell() {
  const mode = useModeStore((s) => s.mode);
  const tenantId = useTenantStore((s) => s.tenantId);
  const userId = useUserStore((s) => s.userId);
  const activePatientId = usePatientStore((s) => s.activePatientId);
  const location = useLocation();
  const activeTab = pathToTabId(location.pathname);

  // Derive the breadcrumb pathway label from the active patient's
  // pathway. The demo cycles across all four pathways, so the label has
  // to track the active selection.
  const pathwayName = useMemo(() => {
    if (!activePatientId) return 'No active patient';
    const patient = getPatientById(activePatientId);
    if (!patient) return 'No active patient';
    return PATHWAY_DISPLAY_NAMES[patient.pathway] ?? 'Pathway';
  }, [activePatientId]);

  // Track previous tab + switch start time so `tab_change` carries the real
  // latency between intent and render.
  const previousTabRef = useRef<TabId>(activeTab);
  const switchStartRef = useRef<number>(performance.now());
  const [didInit, setDidInit] = useState(false);

  // Fire `app_init` once per mount. `entryTab` (PROD-582) carries the
  // resolved tab id from the entry URL so deep-link sessions are
  // observable even when the first route-change effect short-circuits
  // (from === to). `correlation_id` is minted here and gives downstream
  // sinks a single id to group the mount-time event chain by.
  useEffect(() => {
    if (didInit) return;
    emitTelemetry('app_init', {
      tenantDefault: tenantId,
      mode,
      userId,
      entryTab: activeTab,
      correlation_id: newCorrelationId(),
    });
    setDidInit(true);
  }, [didInit, mode, tenantId, userId, activeTab]);

  // Fire `tab_change` on every route change after the first paint.
  useEffect(() => {
    const from = previousTabRef.current;
    const to = activeTab;
    if (from === to) return;
    const latency = Math.round(performance.now() - switchStartRef.current);
    emitTelemetry('tab_change', {
      from_tab: from,
      to_tab: to,
      latency_ms: latency,
      correlation_id: newCorrelationId(),
    });
    previousTabRef.current = to;
    switchStartRef.current = performance.now();
  }, [activeTab]);

  return (
    <ErrorBoundary>
      <div className="flex min-h-screen flex-col bg-surface-base text-text-primary">
        <TopBar
          pathwayBreadcrumb={{
            pathwayName,
            activeLabel: 'Active',
            versionLabel: 'Draft v0.4',
          }}
          userInitials="ST"
        />
        <TabNav />
        <main
          id="app-shell-main"
          role="main"
          data-active-tab={activeTab}
          className="flex-1 bg-surface-base"
        >
          <Outlet />
        </main>
        <TenantFooterPlaceholder />
        <DemoWalkthrough />
      </div>
    </ErrorBoundary>
  );
}
